import { useTranslation } from 'react-i18next';

export interface TravelersData {
    adults: number;
    children: number;
    infants: number;
    childrenAges?: number[];
}

interface TravelersDropdownProps {
    value: TravelersData;
    onChange: (data: TravelersData) => void;
    isExpanded?: boolean;
}

export function TravelersDropdown({ value, onChange, isExpanded = false }: TravelersDropdownProps) {
    const { t } = useTranslation();

    const updateAdults = (delta: number) => {
        const newAdults = Math.max(1, Math.min(16, value.adults + delta));
        onChange({ ...value, adults: newAdults });
    };

    const updateChildren = (delta: number) => {
        const newChildren = Math.max(0, Math.min(10, value.children + delta));
        onChange({ ...value, children: newChildren });
    };

    const updateInfants = (delta: number) => {
        const newInfants = Math.max(0, Math.min(5, value.infants + delta));
        onChange({ ...value, infants: newInfants });
    };

    if (!isExpanded) {
        return null;
    }

    return (
        <div className="travelers-container">
            {/* Adults Row */}
            <div className="traveler-row">
                <div className="traveler-info">
                    <span className="traveler-type">{t('tripBuilder.adults', 'Adults')}</span>
                    <span className="traveler-hint">{t('tripBuilder.adults_age', 'Ages 13 or above')}</span>
                </div>
                <div className="traveler-stepper">
                    <button 
                        type="button" 
                        onClick={() => updateAdults(-1)} 
                        disabled={value.adults <= 1}
                        aria-label={t('tripBuilder.decrease_adults', 'Decrease adults')}
                    >
                        −
                    </button>
                    <span>{value.adults}</span>
                    <button 
                        type="button" 
                        onClick={() => updateAdults(1)} 
                        disabled={value.adults >= 16}
                        aria-label={t('tripBuilder.increase_adults', 'Increase adults')}
                    >
                        +
                    </button>
                </div>
            </div>

            {/* Children Row */}
            <div className="traveler-row">
                <div className="traveler-info">
                    <span className="traveler-type">{t('tripBuilder.children', 'Children')}</span>
                    <span className="traveler-hint">{t('tripBuilder.children_age', 'Ages 2 – 12')}</span>
                </div>
                <div className="traveler-stepper">
                    <button 
                        type="button" 
                        onClick={() => updateChildren(-1)} 
                        disabled={value.children <= 0}
                        aria-label={t('tripBuilder.decrease_children', 'Decrease children')}
                    >
                        −
                    </button>
                    <span>{value.children}</span>
                    <button 
                        type="button" 
                        onClick={() => updateChildren(1)} 
                        disabled={value.children >= 10}
                        aria-label={t('tripBuilder.increase_children', 'Increase children')}
                    >
                        +
                    </button>
                </div>
            </div>

            {/* Infants Row */}
            <div className="traveler-row">
                <div className="traveler-info">
                    <span className="traveler-type">{t('tripBuilder.infants', 'Infants')}</span>
                    <span className="traveler-hint">{t('tripBuilder.infants_age', 'Under 2')}</span>
                </div>
                <div className="traveler-stepper">
                    <button 
                        type="button" 
                        onClick={() => updateInfants(-1)} 
                        disabled={value.infants <= 0}
                        aria-label={t('tripBuilder.decrease_infants', 'Decrease infants')}
                    >
                        −
                    </button>
                    <span>{value.infants}</span>
                    <button 
                        type="button" 
                        onClick={() => updateInfants(1)} 
                        disabled={value.infants >= 5}
                        aria-label={t('tripBuilder.increase_infants', 'Increase infants')}
                    >
                        +
                    </button>
                </div>
            </div>
        </div>
    );
}
